import { ExternalLink, Plane } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";

function formatCost(n) {
  if (typeof n !== "number" || !Number.isFinite(n)) return "—";
  return n.toLocaleString();
}

function formatDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function formatDuration(mins) {
  if (!mins || typeof mins !== "number") return "";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

// Compact "+ $369.00 USD" — seats.aero quotes taxes in the source program's
// native billing currency (USD for AA/UA/DL/LH, CAD for Aeroplan, …),
// which we surface as-is without FX conversion.
function formatTaxes(amount, currency) {
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount <= 0) return "";
  const symbol = { USD: "$", CAD: "C$", AUD: "A$", HKD: "HK$", BRL: "R$", EUR: "€", GBP: "£" }[currency] || "";
  const num = Number.isInteger(amount)
    ? amount.toLocaleString()
    : amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `+ ${symbol}${num}${symbol ? "" : " " + (currency || "")}`;
}

// Highlight pricey taxes/fees: amber over 100, red over 500 (in the fare's
// native currency — a flat threshold, not FX-normalized).
function taxColorClass(amount) {
  if (typeof amount !== "number" || !Number.isFinite(amount)) return "";
  if (amount > 500) return "text-red-600 font-semibold";
  if (amount > 100) return "text-amber-700 font-semibold";
  return "";
}

// Cash equivalent (SerpAPI Google Flights) and CPP (cents-per-point) shown
// below the miles price. CPP > 2 ¢ is a strong redemption; < 1 ¢ is weak.
function formatCash(price, currency) {
  if (typeof price !== "number" || !Number.isFinite(price) || price <= 0) return "";
  const sym = currency === "USD" || !currency ? "$" : "";
  return `${sym}${Math.round(price).toLocaleString()}${sym ? "" : " " + currency}`;
}
function formatCpp(cpp) {
  if (typeof cpp !== "number" || !Number.isFinite(cpp) || cpp <= 0) return "";
  return `${cpp.toFixed(1)}¢/pt`;
}
function cppColorClass(cpp) {
  if (typeof cpp !== "number" || !Number.isFinite(cpp) || cpp <= 0) return "text-neutral-400";
  if (cpp >= 2.5) return "text-emerald-700 font-semibold";
  if (cpp >= 1.5) return "text-emerald-600 font-semibold";
  if (cpp >= 1.0) return "text-neutral-600";
  return "text-amber-700";
}
function CashCppLine({ cashPrice, cashCurrency, cpp }) {
  const cash = formatCash(cashPrice, cashCurrency);
  const cppStr = formatCpp(cpp);
  if (!cash && !cppStr) return null;
  return (
    <div className="text-[11px] mt-0.5 whitespace-nowrap">
      {cash && (
        <span className="tabular text-neutral-600" title="Google Flights cash price for this date and cabin">
          {cash} cash
        </span>
      )}
      {cash && cppStr && <span className="text-neutral-300"> · </span>}
      {cppStr && (
        <span className={`tabular ${cppColorClass(cpp)}`} title="Cents per point — value of using miles vs. paying cash">
          {cppStr}
        </span>
      )}
    </div>
  );
}

const CABIN_NAMES = { economy: "Economy", premium: "Premium Economy", business: "Business", first: "First" };

// "ORD → ZRH · Business · Jun 22 – 28, 2026" — describes what the table reflects.
function querySummary(query) {
  if (!query) return "";
  const parts = [];
  if (query.origin && query.destination) parts.push(`${query.origin} → ${query.destination}`);
  const cabin = (query.cabin || "").toLowerCase();
  parts.push(cabin && CABIN_NAMES[cabin] ? CABIN_NAMES[cabin] : "All cabins");
  if (query.passengers > 1) parts.push(`${query.passengers} travelers`);
  const range = formatRange(query.start_date, query.end_date);
  if (range) parts.push(range);
  return parts.join("  ·  ");
}

function formatRange(start, end) {
  const ds = start ? new Date(start) : null;
  const de = end ? new Date(end) : null;
  const valid = (d) => d && !Number.isNaN(d.getTime());
  if (!valid(ds) && !valid(de)) return "";
  const opt = { month: "short", day: "numeric" };
  if (valid(ds) && valid(de)) {
    const sameYear = ds.getUTCFullYear() === de.getUTCFullYear();
    const sameMonth = sameYear && ds.getUTCMonth() === de.getUTCMonth();
    const left = ds.toLocaleDateString(undefined, { ...opt, timeZone: "UTC" });
    const right = (sameMonth ? de.getUTCDate() : de.toLocaleDateString(undefined, { ...opt, timeZone: "UTC" }));
    return `${left} – ${right}, ${de.getUTCFullYear()}`;
  }
  const one = (valid(ds) ? ds : de);
  return one.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// Cabin columns shown when the backend returned a cabin_prices map (cabin=any).
// We only render columns that at least one row actually has availability in.
const CABIN_COLS = [
  { key: "economy",  label: "Economy" },
  { key: "premium",  label: "Prem. econ." },
  { key: "business", label: "Business" },
  { key: "first",    label: "First" },
];

// Compact one-line meta under a price, e.g. "+ C$90.30 · 9+ seats · nonstop".
function MetaText({ taxAmount, taxCurrency, seats, direct }) {
  const tax = formatTaxes(taxAmount, taxCurrency); // "+ C$90.30" (empty when none)
  const seatStr = seats ? `${seats > 8 ? "9+" : seats} seats` : "";
  const segs = [];
  if (tax) segs.push(<span key="t" className={`tabular ${taxColorClass(taxAmount)}`}>{tax}</span>);
  if (seatStr) segs.push(<span key="s">{seatStr}</span>);
  if (direct) segs.push(<span key="d" className="text-emerald-700">nonstop</span>);
  if (segs.length === 0) return null;
  const out = [];
  segs.forEach((s, i) => {
    if (i > 0) out.push(<span key={`sep${i}`} className="text-neutral-300"> · </span>);
    out.push(s);
  });
  return <div className="text-[11px] text-neutral-500 mt-0.5 whitespace-nowrap">{out}</div>;
}

// Per-cabin routing line ("nonstop · 9h 5m · AA150" / "1 stop · 10h 10m ·
// KL612, KL1405"). Each cabin column can be a different itinerary, so this
// lives in the cell, not the shared Route column.
function CabinRouting({ p }) {
  const direct = !!p.direct;
  const dur = p.duration_min ? formatDuration(p.duration_min) : "";
  const head = direct
    ? "nonstop"
    : (typeof p.stops === "number" ? `${p.stops} stop${p.stops === 1 ? "" : "s"}` : "");
  const tail = [dur, p.flight_numbers].filter(Boolean).join(" · ");
  if (!head && !tail) return null;
  const line = (
    <span>
      {head && <span className={direct ? "text-emerald-700" : undefined}>{head}</span>}
      {head && tail ? " · " : ""}
      {tail}
    </span>
  );
  if (!p.segments?.length) {
    return <div className="text-[10.5px] mt-0.5 whitespace-nowrap text-neutral-500">{line}</div>;
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="text-[10.5px] mt-0.5 whitespace-nowrap text-neutral-500 cursor-help underline decoration-dotted decoration-neutral-300 underline-offset-2 inline-block">
          {line}
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-neutral-900 text-white border-none shadow-lg p-2.5">
        <LegsTooltip segments={p.segments} connections={p.connections} />
      </TooltipContent>
    </Tooltip>
  );
}

// A single per-cabin price cell. `p` is a cabin_prices[key] entry, or null.
function CabinCell({ p, currency }) {
  if (!p || !(p.mileage_cost > 0)) {
    return <td className="px-3 py-1.5 text-right align-top text-neutral-300">—</td>;
  }
  return (
    <td className="px-3 py-1.5 text-right align-top whitespace-nowrap">
      <div className="font-semibold tabular">
        {formatCost(p.mileage_cost)} <span className="font-normal text-neutral-400 text-[11px]">{currency}</span>
      </div>
      <MetaText taxAmount={p.taxes_amount} taxCurrency={p.taxes_currency} seats={p.remaining_seats} />
      <CashCppLine cashPrice={p.cash_price} cashCurrency={p.cash_currency} cpp={p.cpp} />
      <CabinRouting p={p} />
    </td>
  );
}

// Single "Cost" cell used for a specific-cabin search (no cabin matrix).
function CostCell({ f }) {
  const currency = f.currency || "pts";
  return (
    <td className="px-3 py-1.5 text-right align-top whitespace-nowrap">
      <div className="font-semibold tabular">
        {formatCost(f.mileage_cost)} <span className="font-normal text-neutral-400 text-[11px]">{currency}</span>
      </div>
      <MetaText taxAmount={f.taxes_amount} taxCurrency={f.taxes_currency} seats={f.remaining_seats} direct={f.direct} />
      <CashCppLine cashPrice={f.cash_price} cashCurrency={f.cash_currency} cpp={f.cpp} />
    </td>
  );
}

// Layovers at/above this are flagged as a rough connection.
const LONG_LAYOVER_MIN = 8 * 60;

function fmtDistance(d) {
  const n = Number(d);
  return Number.isFinite(n) && n > 0 ? `${n.toLocaleString()} mi` : "";
}

// seats.aero labels its timestamps "Z" but the values are LOCAL airport
// wall-clock times, so we read the HH:MM as-is (no timezone conversion).
function localTime(iso) {
  const m = String(iso || "").match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : "";
}

// Calendar days between a departure and arrival date string (for "+1" arrivals).
function dayOffset(dep, arr) {
  const d = String(dep || "").slice(0, 10);
  const a = String(arr || "").slice(0, 10);
  if (!d || !a || d === a) return 0;
  const ms = Date.parse(`${a}T00:00:00Z`) - Date.parse(`${d}T00:00:00Z`);
  return Number.isFinite(ms) ? Math.round(ms / 86400000) : 0;
}

// Hover content: leg-by-leg breakdown with departure/arrival times and the
// layover between each.
function LegsTooltip({ segments, connections }) {
  if (!segments?.length) return null;
  return (
    <div className="text-[11px] leading-snug max-w-[300px]">
      {segments.map((s, i) => {
        const c = i > 0 ? (connections || [])[i - 1] : null;
        const long = c && c.layover_min >= LONG_LAYOVER_MIN;
        const meta = [
          s.duration_min ? formatDuration(s.duration_min) : "",
          s.aircraft,
          fmtDistance(s.distance),
        ].filter(Boolean).join(" · ");
        const dep = localTime(s.departs_at);
        const arr = localTime(s.arrives_at);
        const plus = dayOffset(s.departs_at, s.arrives_at);
        return (
          <div key={i}>
            {c && (
              <div className={`my-1 ${long ? "text-amber-300" : "text-neutral-400"}`}>
                ↕ {c.layover_min ? `${formatDuration(c.layover_min)} ` : ""}layover{c.airport ? ` · ${c.airport}` : ""}
              </div>
            )}
            <div className="font-semibold text-white">
              {s.flight_number || "—"} · {s.origin || "?"} → {s.destination || "?"}
            </div>
            {(dep || arr) && (
              <div className="text-neutral-300 tabular">
                {(dep || "—")} {s.origin} → {(arr || "—")} {s.destination}
                {plus > 0 ? <span className="text-amber-300"> +{plus}</span> : ""}
              </div>
            )}
            {meta && <div className="text-neutral-400">{meta}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Routing as a path ("ORD → AMS → CDG"); hover reveals the per-leg breakdown.
// Falls back to a plain origin→destination span when there are no segments.
function RoutePath({ segments, origin, destination, connections, className = "" }) {
  const hasLegs = Array.isArray(segments) && segments.length > 0;
  const stops = hasLegs
    ? [segments[0].origin, ...segments.map((s) => s.destination)]
    : [origin, destination];
  const pathStr = stops.filter(Boolean).join(" → ") || "—";
  if (!hasLegs) return <span className={className}>{pathStr}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={`${className} cursor-help underline decoration-dotted decoration-neutral-400/60 underline-offset-2`}>
          {pathStr}
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-neutral-900 text-white border-none shadow-lg p-2.5">
        <LegsTooltip segments={segments} connections={connections} />
      </TooltipContent>
    </Tooltip>
  );
}

function RouteCell({ f }) {
  // In matrix mode each cabin has its own routing (shown in the cabin cells),
  // so the shared Route column only states the city pair to avoid implying one
  // routing applies to all cabins.
  const matrix = !!f.cabin_prices;
  return (
    <td className="px-3 py-1.5 align-top">
      <RoutePath
        segments={f.segments}
        origin={f.origin}
        destination={f.destination}
        connections={f.connections}
        className="font-mono text-[12.5px]"
      />
      {!matrix && (
        <div className="text-[11px] text-neutral-500 mt-0.5">
          {f.direct ? "Nonstop" : (typeof f.stops === "number" ? `${f.stops} stop${f.stops === 1 ? "" : "s"}` : "")}
          {f.duration_min ? ` · ${formatDuration(f.duration_min)}` : ""}
          {f.flight_numbers ? ` · ${f.flight_numbers}` : ""}
        </div>
      )}
    </td>
  );
}

function ProgramCell({ f }) {
  // transfer_from_label may be a list (one entry per issuer that can transfer
  // to this program) or a string (older persisted threads).
  const labels = Array.isArray(f.transfer_from_label)
    ? f.transfer_from_label
    : (f.transfer_from_label ? [f.transfer_from_label] : []);
  return (
    <td className="px-3 py-1.5 align-top">
      <div className="font-semibold">{f.program || f.source || "—"}</div>
      {labels.length > 0 && (
        <div className="text-[11px] text-amber-700 mt-0.5">
          via {labels.join(" / ")} transfer
        </div>
      )}
      {f.bookable_with === "direct" && (
        <div className="text-[11px] text-emerald-700 mt-0.5">direct balance</div>
      )}
    </td>
  );
}

function BookCell({ f, i }) {
  return (
    <td className="px-3 py-1.5 text-right align-top">
      {f.booking_link ? (
        <a
          href={f.booking_link}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary text-[12.5px] inline-flex items-center gap-1"
          data-testid={`trip-book-${i}`}
        >
          Book <ExternalLink size={12} />
        </a>
      ) : (
        <span className="text-[11px] text-neutral-400">no link</span>
      )}
    </td>
  );
}

/**
 * Renders the ranked award-flight table. Returns null when there are no
 * flights so callers can fall back to their own empty state.
 *
 * When the backend returned per-cabin pricing (cabin=any), each available
 * cabin gets its own column so a single trip stays on one row. For a
 * specific-cabin search there's a single Cost column instead.
 */
export default function FlightResults({
  flights,
  query,
  status,
  hideRoute = false,
  bookRender,         // (f, i) => ReactNode — replaces the default Book cell when set
  hideFooterNote = false,
}) {
  if (!flights || flights.length === 0) return null;
  const summary = querySummary(query);

  // Cabin-matrix mode: any row carries a cabin_prices map. Show every cabin
  // column so the grid is complete; unavailable cabins render as "—".
  const matrix = flights.some((f) => !!f.cabin_prices);
  const cabinCols = CABIN_COLS;

  // Specific-cabin search: name the class in the Cost header so it can be
  // dropped from each row.
  const singleCabin = !matrix ? (query?.cabin || flights[0]?.cabin || "").toLowerCase() : "";
  const costHeader = singleCabin
    ? `Cost (${CABIN_NAMES[singleCabin] || singleCabin})`
    : "Cost";

  return (
    <TooltipProvider delayDuration={150}>
    <div data-testid="trip-flights">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mb-2">
        <div className="flex items-center gap-1.5 text-[12px] font-semibold text-neutral-700 uppercase tracking-wide">
          <Plane size={12} /> Top {flights.length} bookable option{flights.length === 1 ? "" : "s"}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {summary && (
            <div className="text-[12px] text-neutral-500 font-medium" data-testid="trip-results-summary">
              {summary}
            </div>
          )}
          {status}
        </div>
      </div>
      <div className="overflow-x-auto border border-black/5 rounded-[10px] bg-white">
        <table className="w-full text-[13px]">
          <thead className="bg-neutral-50 text-[11.5px] uppercase tracking-wide text-neutral-500">
            <tr>
              <th className="text-left font-semibold px-3 py-1.5">Program</th>
              {!hideRoute && <th className="text-left font-semibold px-3 py-1.5">Route</th>}
              <th className="text-left font-semibold px-3 py-1.5">Date</th>
              {matrix ? (
                cabinCols.map(({ key, label }) => (
                  <th key={key} className="text-right font-semibold px-3 py-1.5 whitespace-nowrap">{label}</th>
                ))
              ) : (
                <th className="text-right font-semibold px-3 py-1.5 whitespace-nowrap">{costHeader}</th>
              )}
              <th className="text-right font-semibold px-3 py-1.5"></th>
            </tr>
          </thead>
          <tbody>
            {flights.map((f, i) => (
              <tr key={i} className="border-t border-black/5">
                <ProgramCell f={f} />
                {!hideRoute && <RouteCell f={f} />}
                <td className="px-3 py-1.5 whitespace-nowrap align-top">{formatDate(f.date)}</td>
                {matrix ? (
                  cabinCols.map(({ key }) => (
                    <CabinCell key={key} p={f.cabin_prices?.[key]} currency={f.currency || "pts"} />
                  ))
                ) : (
                  <CostCell f={f} />
                )}
                {bookRender ? (
                  <td className="px-3 py-1.5 text-right align-top">{bookRender(f, i)}</td>
                ) : (
                  <BookCell f={f} i={i} />
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hideFooterNote && (
        <div className="text-[11px] text-neutral-500 mt-1.5">
          {matrix
            ? "Cheapest cabin shown per column · taxes in the program's billing currency. Filtered to programs you can book with."
            : "Filtered to programs you can book with (direct balance or transferable currency)."}
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}
